import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowDownTrayIcon, ArrowPathIcon, ShareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  applyUpdate,
  canInstallNative,
  canShowIosInstallHelp,
  closeIosInstallHelp,
  dismissInstallPrompt,
  getWaitingWorker,
  isIosInstallHelpOpen,
  isStandaloneDisplay,
  promptInstall,
  requestIosInstallHelp,
  subscribePwa,
} from '../pwa/runtime';

const PwaShell: React.FC = () => {
  const location = useLocation();
  const [, setTick] = useState(0);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    return subscribePwa(() => {
      setTick((n) => n + 1);
      setOnline(navigator.onLine);
    });
  }, []);

  const waiting = Boolean(getWaitingWorker());
  const installNative = canInstallNative();
  const iosHelp = canShowIosInstallHelp();
  const standalone = isStandaloneDisplay();
  const showInstall = !waiting && !standalone && (installNative || iosHelp);
  const showIosHelp = isIosInstallHelpOpen();
  const isAdminMobile = location.pathname.startsWith('/admin');
  const bottomClass = isAdminMobile
    ? 'bottom-[calc(4.25rem+env(safe-area-inset-bottom,0px))] md:bottom-4'
    : 'bottom-4';

  return (
    <>
      {!online && (
        <div
          className="fixed top-0 left-0 right-0 z-[70] bg-amber-600 text-white text-sm text-center px-3 py-2 shadow safe-area-pt"
          role="status"
        >
          Sin conexión. Puedes seguir navegando, pero los datos no se actualizarán.
        </div>
      )}

      {waiting && (
        <div className={`fixed inset-x-0 z-[65] px-3 md:left-auto md:right-4 md:inset-x-auto md:w-96 ${bottomClass}`}>
          <div className="rounded-xl bg-scout-green text-white shadow-lg p-3 flex items-start gap-3">
            <ArrowPathIcon className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Hay una nueva versión</p>
              <p className="text-xs text-white/80 mt-0.5">Actualiza para cargar la última versión de Tesorería.</p>
              <button
                type="button"
                onClick={applyUpdate}
                className="mt-2 rounded-lg bg-white text-scout-green px-3 py-1.5 text-sm font-semibold hover:bg-scout-cream"
              >
                Actualizar ahora
              </button>
            </div>
          </div>
        </div>
      )}

      {showInstall && (
        <div className={`fixed inset-x-0 z-[60] px-3 md:left-auto md:right-4 md:inset-x-auto md:w-96 ${bottomClass}`}>
          <div className="rounded-xl bg-white border border-scout-green/20 shadow-lg p-3 flex items-start gap-3">
            <ArrowDownTrayIcon className="h-5 w-5 text-scout-green shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-scout-green-dark">Instalar Tesorería</p>
              <p className="text-xs text-slate-600 mt-0.5">
                Añádela a tu dispositivo para abrirla como una aplicación.
              </p>
              <div className="mt-2 flex items-center gap-2">
                {installNative ? (
                  <button
                    type="button"
                    onClick={() => {
                      void promptInstall();
                    }}
                    className="rounded-lg bg-scout-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-scout-green-dark"
                  >
                    Instalar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={requestIosInstallHelp}
                    className="rounded-lg bg-scout-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-scout-green-dark"
                  >
                    Cómo instalar
                  </button>
                )}
                <button
                  type="button"
                  onClick={dismissInstallPrompt}
                  className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1.5"
                >
                  Ahora no
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissInstallPrompt}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
              aria-label="Cerrar"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showIosHelp && (
        <div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/50 p-0 md:p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-md p-5 shadow-xl safe-area-pb">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-base font-semibold text-scout-green-dark">Añadir a pantalla de inicio</h2>
              <button type="button" onClick={closeIosInstallHelp} className="p-1 text-slate-400" aria-label="Cerrar">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <ol className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2">
                <span className="font-semibold text-scout-green">1.</span>
                <span className="flex items-center gap-1 flex-wrap">
                  Pulsa el botón Compartir
                  <ShareIcon className="h-4 w-4 text-scout-green inline" />
                  en Safari.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-scout-green">2.</span>
                <span>Elige <strong>Añadir a pantalla de inicio</strong>.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-scout-green">3.</span>
                <span>Confirma con <strong>Añadir</strong>.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={closeIosInstallHelp}
              className="mt-4 w-full rounded-lg bg-scout-green py-2.5 text-sm font-semibold text-white"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaShell;
